import { useState, useEffect, useRef } from 'react'
import { Search, X } from 'lucide-react'
import { StyledSelect } from '../ui/StyledSelect'

export interface FeedbackFilterState {
  type: string | null
  status: string | null
  mine: boolean
  search: string
  sort: string
}

interface FeedbackFiltersProps {
  filters: FeedbackFilterState
  onChange: (filters: FeedbackFilterState) => void
  isAdmin?: boolean
}

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature' },
  { value: 'improvement', label: 'Improvement' },
  { value: 'integration', label: 'Integration' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'backlog', label: 'Backlog' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
]

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'most_voted', label: 'Most Voted' },
]

export function FeedbackFilters({ filters, onChange, isAdmin: _isAdmin, children }: FeedbackFiltersProps & { children?: React.ReactNode }) {
  const [searchInput, setSearchInput] = useState(filters.search)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      if (searchInput !== filters.search) {
        onChange({ ...filters, search: searchInput })
      }
    }, 300)
    return () => clearTimeout(debounceRef.current)
  }, [searchInput]) // eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch: Partial<FeedbackFilterState>) => {
    onChange({ ...filters, ...patch })
  }

  return (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
      {/* Search — full width on mobile, constrained on desktop (matches MyApps) */}
      <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search feedback..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full pl-10 pr-8 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
        />
        {searchInput && (
          <button
            onClick={() => { setSearchInput(''); update({ search: '' }) }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-secondary"
          >
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Type filter */}
      <StyledSelect
        value={filters.type || ''}
        onChange={(val) => update({ type: val || null })}
        options={TYPE_OPTIONS}
      />

      {/* Status filter */}
      <StyledSelect
        value={filters.status || ''}
        onChange={(val) => update({ status: val || null })}
        options={STATUS_OPTIONS}
      />

      {/* Sort */}
      <StyledSelect
        value={filters.sort}
        onChange={(val) => update({ sort: val })}
        options={SORT_OPTIONS}
      />

      {/* My Submissions toggle */}
      <button
        onClick={() => update({ mine: !filters.mine })}
        className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
          filters.mine
            ? 'bg-primary text-primary-foreground border-primary'
            : 'bg-background border-border hover:border-foreground/20'
        }`}
      >
        My Submissions
      </button>

      {children}
    </div>
  )
}
